import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import * as React from 'react';
// @mui
import { 
  Container, Stack, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Box,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
// Tabela
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import { UserListToolbar } from '../sections/@dashboard/user';
import Iconify from '../components/iconify';

// api
import { getIndices, updateIndices } from './api';

// ── Índices que exigem termo de responsabilidade ao alterar ───────────────────
const INDICES_COM_TERMO = [
  'Performance Ratio (PR)',
  'DoD (Off-Grid)',
  'RoundTrip Efficiency (Off-Grid)',
];

// ── Textos do termo por índice ────────────────────────────────────────────────
const TERMO_TEXTS = {
  'Performance Ratio (PR)': {
    titulo: 'Alteração do Performance Ratio (PR)',
    descricao: (
      <>
        <Typography variant="body2" gutterBottom>
          O <strong>Performance Ratio (PR)</strong> é o fator de desempenho utilizado no cálculo
          de geração fotovoltaica de todas as propostas. Seu valor padrão de{' '}
          <strong>0,75 (75%)</strong> foi obtido de forma empírica ao longo de anos de análise.
        </Typography>
        <Typography variant="body2" gutterBottom>
          A alteração inadequada deste parâmetro pode ocasionar:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 1 }}>
          <Typography component="li" variant="body2">Dimensionamentos inadequados dos sistemas fotovoltaicos</Typography>
          <Typography component="li" variant="body2">Estimativas de geração incorretas nas propostas comerciais</Typography>
          <Typography component="li" variant="body2">Comprometimento da credibilidade técnica das propostas</Typography>
        </Box>
      </>
    ),
  },
  'DoD (Off-Grid)': {
    titulo: 'Alteração do DoD — Depth of Discharge (Off-Grid)',
    descricao: (
      <>
        <Typography variant="body2" gutterBottom>
          O <strong>DoD (Depth of Discharge)</strong> define o percentual máximo de descarga
          permitido para as baterias dos sistemas off-grid. Seu valor padrão é{' '}
          <strong>0,90 (90%)</strong>.
        </Typography>
        <Typography variant="body2" gutterBottom>
          A alteração inadequada deste parâmetro pode ocasionar:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 1 }}>
          <Typography component="li" variant="body2">Subdimensionamento da capacidade de armazenamento</Typography>
          <Typography component="li" variant="body2">Redução da vida útil das baterias</Typography>
          <Typography component="li" variant="body2">Autonomia noturna insuficiente para o cliente</Typography>
        </Box>
      </>
    ),
  },
  'RoundTrip Efficiency (Off-Grid)': {
    titulo: 'Alteração do RoundTrip Efficiency (Off-Grid)',
    descricao: (
      <>
        <Typography variant="body2" gutterBottom>
          O <strong>RoundTrip Efficiency</strong> representa a eficiência do ciclo completo
          de carga e descarga das baterias off-grid. Seu valor padrão é{' '}
          <strong>0,90 (90%)</strong>.
        </Typography>
        <Typography variant="body2" gutterBottom>
          A alteração inadequada deste parâmetro pode ocasionar:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 1 }}>
          <Typography component="li" variant="body2">Cálculo incorreto da autonomia líquida das baterias</Typography>
          <Typography component="li" variant="body2">Seleção de kits inadequados para o consumo noturno</Typography>
          <Typography component="li" variant="body2">Comprometimento da qualidade técnica do dimensionamento</Typography>
        </Box>
      </>
    ),
  },
};

// ── Componente ────────────────────────────────────────────────────────────────
export default function Indices() {
  const [indices, setIndices]         = useState([]);
  const [editingRow, setEditingRow]   = useState(null);
  const [originalRow, setOriginalRow] = useState(null);
  const [newRow, setNewRow]           = useState(null);
  const [modalTermo, setModalTermo]   = useState(false);
  const [pendingSave, setPendingSave] = useState(null);
  const [termoAtivo, setTermoAtivo]   = useState(null); // nome do índice com termo aberto

  useEffect(() => {
    const fetchIndices = async () => {
      const data = await getIndices();
      setIndices(data);
    };
    fetchIndices();
  }, []);

  const [filterValue, setFilterValue] = useState('');
  const filtered = indices.filter((row) =>
    row.nome?.toLowerCase().includes(filterValue.toLowerCase())
  );
  const handleFilter = (e) => setFilterValue(e.target.value);

  const handleAdd = () => setNewRow({ nome: '', valor: 0 });

  const handleEdit = (index) => {
    setOriginalRow({ ...indices[index] });
    setEditingRow(index);
  };

  const handleCancelEdit = () => {
    if (newRow !== null) {
      setNewRow(null);
    } else {
      const updated = [...indices];
      updated[editingRow] = originalRow;
      setIndices(updated);
      setOriginalRow(null);
    }
    setEditingRow(null);
  };

  const executarSave = async (row, index) => {
    await updateIndices(row);
    const updated = [...indices];
    if (newRow !== null) {
      updated.push(row);
      setNewRow(null);
    } else {
      updated[index] = row;
    }
    setIndices(updated);
    setEditingRow(null);
    setOriginalRow(null);
  };

  const handleSaveEdit = async (row, index) => {
    // Verifica se o índice exige termo de responsabilidade
    if (INDICES_COM_TERMO.includes(row.nome)) {
      setPendingSave({ row, index });
      setTermoAtivo(row.nome);
      setModalTermo(true);
      return;
    }
    await executarSave(row, index);
  };

  const handleAceitarTermo = async () => {
    setModalTermo(false);
    setTermoAtivo(null);
    if (pendingSave) {
      await executarSave(pendingSave.row, pendingSave.index);
      setPendingSave(null);
    }
  };

  const handleRecusarTermo = () => {
    setModalTermo(false);
    setTermoAtivo(null);
    setPendingSave(null);
    handleCancelEdit();
  };

  const handleChange = (e, key, row) => {
    const { value } = e.target;
    row[key] = key === 'valor' ? parseFloat(value) : value;
    if (newRow !== null) {
      setNewRow({ ...newRow, [key]: row[key] });
    } else {
      setIndices([...indices]);
    }
  };

  // Texto do termo ativo
  const termoInfo = termoAtivo ? TERMO_TEXTS[termoAtivo] : null;

  return (
    <>
      <Helmet>
        <title> Índices </title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction={'row'} justifyContent={'space-between'}>
          <UserListToolbar filterName={filterValue} onFilterName={handleFilter} sx={{ width: '100%' }} />
          <Button
            variant="contained"
            startIcon={<Iconify icon="zondicons:add-solid" />}
            onClick={handleAdd}
            sx={{ height: 50, minWidth: 100 }}
          >
            Adicionar
          </Button>
        </Stack>

        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="indices table">
            <TableHead>
              <TableRow>
                <TableCell align="left">Nome</TableCell>
                <TableCell align="center">Valor</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: 'Azure' } }}
                >
                  <TableCell align="left">
                    {editingRow === index ? (
                      <TextField value={row.nome} onChange={(e) => handleChange(e, 'nome', row)} />
                    ) : (
                      <Stack direction="row" alignItems="center" gap={1}>
                        {row.nome}
                        {INDICES_COM_TERMO.includes(row.nome) && (
                          <WarningAmberIcon sx={{ color: '#e65100', fontSize: 16 }} titleAccess="Requer termo de responsabilidade ao alterar" />
                        )}
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {editingRow === index ? (
                      <TextField value={row.valor} type="number" onChange={(e) => handleChange(e, 'valor', row)} />
                    ) : (
                      row.valor
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {editingRow === index ? (
                      <>
                        <Button onClick={() => handleSaveEdit(row, index)}>Salvar</Button>
                        <Button onClick={handleCancelEdit}>Cancelar</Button>
                      </>
                    ) : (
                      editingRow !== null ? null : (
                        <Button onClick={() => handleEdit(index)}>Editar</Button>
                      )
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {newRow && (
                <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { backgroundColor: 'Azure' } }}>
                  <TableCell align="left">
                    <TextField value={newRow.nome} onChange={(e) => handleChange(e, 'nome', newRow)} />
                  </TableCell>
                  <TableCell align="center">
                    <TextField value={newRow.valor} type="number" onChange={(e) => handleChange(e, 'valor', newRow)} />
                  </TableCell>
                  <TableCell align="center">
                    <Button onClick={() => handleSaveEdit(newRow)}>Salvar</Button>
                    <Button onClick={handleCancelEdit}>Cancelar</Button>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      {/* ── Modal Termo de Responsabilidade ── */}
      <Dialog open={modalTermo} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" gap={1}>
            <WarningAmberIcon sx={{ color: '#e65100', fontSize: 28 }} />
            <Typography variant="h6" sx={{ color: '#e65100', fontWeight: 'bold' }}>
              Termo de Responsabilidade
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {termoInfo && (
            <Box sx={{ backgroundColor: '#fff3e0', border: '1.5px solid #e65100', borderRadius: 2, p: 2, mb: 2 }}>
              <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 'bold' }} gutterBottom>
                ATENÇÃO — {termoInfo.titulo}
              </Typography>
              {termoInfo.descricao}
              <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                A responsabilidade pela alteração deste parâmetro é única e exclusiva do usuário.
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary">
            Ao clicar em <strong>"Estou ciente, salvar"</strong>, você confirma que leu e
            compreendeu os riscos e assume total responsabilidade pelos resultados.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" color="error" onClick={handleRecusarTermo}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAceitarTermo}
            sx={{ backgroundColor: '#e65100', '&:hover': { backgroundColor: '#bf360c' } }}
          >
            Estou ciente, salvar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}